import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChatMessage, PaginatedResponse } from '@klaro7/shared-types';

import {
  getAdminConversation,
  getAdminConversationFacets,
  getAdminConversations,
  getAdminStarred,
  getAdminStarredFacets,
  starMessage,
  unstarMessage,
  type AdminConversationsFilters,
  type AdminStarredFilters,
} from '../services/starred.service';

type MessagesPage = PaginatedResponse<ChatMessage>;

/**
 * Stella / togli stella su un messaggio della conversazione aperta, con
 * aggiornamento ottimistico della cache ['chat-messages', conversationId]
 * e rollback in caso di errore.
 */
export function useToggleStar(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  const key = ['chat-messages', conversationId];

  return useMutation({
    mutationFn: ({ messageId, starred }: { messageId: string; starred: boolean }) =>
      starred ? starMessage(messageId) : unstarMessage(messageId),
    onMutate: async ({ messageId, starred }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<MessagesPage>(key);
      queryClient.setQueryData<MessagesPage>(key, (old) =>
        old
          ? { ...old, data: old.data.map((m) => (m.id === messageId ? { ...m, isStarred: starred } : m)) }
          : old
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: ['admin-starred'] });
    },
  });
}

export function useAdminStarred(filters: AdminStarredFilters) {
  return useQuery({
    queryKey: ['admin-starred', filters],
    queryFn: () => getAdminStarred(filters),
    placeholderData: keepPreviousData,
  });
}

export function useAdminStarredFacets() {
  return useQuery({
    queryKey: ['admin-starred', 'facets'],
    queryFn: getAdminStarredFacets,
    staleTime: 60_000,
  });
}

export function useAdminConversation(conversationId: string | null) {
  return useQuery({
    queryKey: ['admin-chat-conversation', conversationId],
    queryFn: () => getAdminConversation(conversationId!),
    enabled: !!conversationId,
  });
}

export function useAdminConversations(filters: AdminConversationsFilters) {
  return useQuery({
    queryKey: ['admin-conversations', filters],
    queryFn: () => getAdminConversations(filters),
    placeholderData: keepPreviousData,
  });
}

export function useAdminConversationFacets() {
  return useQuery({
    queryKey: ['admin-conversations', 'facets'],
    queryFn: getAdminConversationFacets,
    staleTime: 60_000,
  });
}
